import { ApolloLink, Operation, NextLink, DocumentNode } from 'apollo-link'
import {
  hasDirectives,
  isInlineFragment,
  isField,
  FragmentMap,
  createFragmentMap,
  getFragmentDefinitions,
  getMainDefinition,
} from 'apollo-utilities'
import {
  DirectiveNode,
  SelectionSetNode,
  FieldNode,
  FragmentSpreadNode,
  FragmentDefinitionNode,
  visit,
} from 'graphql'
import localStorageKeys from 'constants/localStorageKeys'

const CYPRESS_DIRECTIVE = 'cypress'

const nodeHasCypressDirective = (
  field: FieldNode | FragmentSpreadNode
): boolean => {
  if (field.directives && field.directives.length) {
    return field.directives.some(
      directive => CYPRESS_DIRECTIVE === directive.name.value
    )
  }
  return false
}

const replaceAllFragmentFieldsWithNull = (
  data: any,
  fragment: FragmentDefinitionNode
) => {
  fragment.selectionSet.selections.forEach(selection => {
    if (selection.kind === 'Field') {
      data[selection.name.value] = null
    }
  })
}

/**
 * Applies an update function to a data object. In Apollo's terms, a field can
 * be either a regular object or an array of objects. This function deals with
 * those scenarios and applies the function to whatever data is
 */
const applyUpdateToField = (data: any, update: (item: any) => void) => {
  if (Array.isArray(data)) {
    data.forEach(item => update(item))
  } else {
    update(data)
  }
}

/**
 * This function traverses both the response data and the selectionSet
 * recursively until it finds a field with the @cypress directive. At that
 * point, it just sets the field value to null.
 * @param data Apollo response data. An object with no defined structure
 * @param fragmentMap Map with all the fragments used in the current operation
 * @param currentSelectionSet Apollo's selection set. It's a tree-like structure
 *                            that holds information about the operation fields
 *                            like its directives
 */
const replaceCypressDirectiveFieldsWithNull = (
  data: any,
  fragmentMap: FragmentMap,
  currentSelectionSet?: SelectionSetNode
) => {
  // We don't need to traverse plain types
  if (
    currentSelectionSet == null ||
    null == data ||
    typeof data === 'number' ||
    typeof data === 'boolean' ||
    typeof data === 'string'
  ) {
    return data
  }

  currentSelectionSet.selections.forEach(node => {
    if (isInlineFragment(node)) {
      /**
       * For inline fragments, we just pick its selection sets and continue
       * looking for a match between selection sets and the returned data
       */
      replaceCypressDirectiveFieldsWithNull(
        data,
        fragmentMap,
        node.selectionSet
      )
    } else if (node.kind === 'FragmentSpread') {
      /**
       * For FragmentSpread nodes:
       *
       * 1. If fragment spread has the directive, we first set all fields from
       *    the fragment to null.
       * 2. Then we recursively call this function again to make sure we deal
       *    with with fragments inside fragments.
       */
      const fragment = fragmentMap[node.name.value]

      if (nodeHasCypressDirective(node)) {
        replaceAllFragmentFieldsWithNull(data, fragment)
      }

      replaceCypressDirectiveFieldsWithNull(
        data,
        fragmentMap,
        fragment.selectionSet
      )
    } else if (isField(node)) {
      /**
       * For fields there are two options:
       *
       * 1. The field is marked with the @cypress directive. In that case we
       *    just set that field as null.
       * 2. The field is not marked with the @cypress directive. Therefore, we
       *    recursively look into its children.
       */
      const nodeName = node.alias ? node.alias.value : node.name.value

      if (nodeHasCypressDirective(node)) {
        applyUpdateToField(data, item => (item[nodeName] = null))
      } else {
        applyUpdateToField(data, item =>
          replaceCypressDirectiveFieldsWithNull(
            item[nodeName],
            fragmentMap,
            node.selectionSet
          )
        )
      }
    } else {
      // This will give a TypeScript build-time error if you did something wrong or the AST changes!
      return ((node: never): never => {
        throw new Error('Unhandled Node Type in SelectionSetNode.selections')
      })(node)
    }
  })

  return data
}

export const removeCypressDirectivesFromNode = ({
  node,
  removeFieldsFromNodes,
}: {
  node: DocumentNode
  removeFieldsFromNodes: boolean
}): DocumentNode =>
  visit(node, {
    Directive: {
      enter: (node: DirectiveNode) =>
        node.name.value === CYPRESS_DIRECTIVE ? null : node,
    },
    Field: {
      enter: (node: FieldNode) =>
        removeFieldsFromNodes && nodeHasCypressDirective(node) ? null : node,
    },
    FragmentSpread: {
      enter: (node: FragmentSpreadNode) =>
        removeFieldsFromNodes && nodeHasCypressDirective(node) ? null : node,
    },
  })

/**
 * Traverses all selections recursively and keeps track of all used
 * fragments
 */
const getUsedFragmentNames = (
  selectionSet: SelectionSetNode,
  fragmentMap: FragmentMap
): string[] => {
  // eslint-disable-next-line array-callback-return
  return selectionSet.selections.flatMap(selection => {
    switch (selection.kind) {
      case 'Field':
      case 'InlineFragment':
        if (selection.selectionSet)
          return getUsedFragmentNames(selection.selectionSet, fragmentMap)
        else return []
      case 'FragmentSpread': {
        const fragment = fragmentMap[selection.name.value]

        return [
          selection.name.value,
          ...getUsedFragmentNames(fragment.selectionSet, fragmentMap),
        ]
      }
    }
  })
}

/**
 * We manually remove unused fragment definitions from the query. This step
 * ensures we don't send local fragments that have been discarded while
 * removing the @cypress directive.
 *
 * Imagine we defined a new field in Shares:
 *
 * fragment ShareInfo on Share {
 *   workspace @cypress {
 *     ...WorkspaceMinimal
 *   }
 * }
 *
 * In this case, we are adding a new fragment definition: WorkspaceMinimal.
 * However, this fragment can include fields that are still not defined in the
 * backend and so, we need to prevent its definition to be sent.
 */
const removeUnusedFragmentDefinitions = (node: DocumentNode) => {
  const mainDefinition = getMainDefinition(node)
  const fragmentDefinitions = getFragmentDefinitions(node)
  const fragmentMap = createFragmentMap(fragmentDefinitions)
  const usedFragments = getUsedFragmentNames(
    mainDefinition.selectionSet,
    fragmentMap
  )

  return visit(node, {
    FragmentDefinition: {
      enter: (node: FragmentDefinitionNode) =>
        usedFragments.includes(node.name.value) ? node : null,
    },
  })
}

/**
 * This Apollo Link reads @cypress directives in GraphQL fields and removes them
 * unless they are executed from Cypress tests.
 * Fields with the directive are set to null in the response so that Apollo
 * doesn't complain.
 */
export const createCypressLink = () =>
  new ApolloLink((operation: Operation, forward?: NextLink) => {
    const { query } = operation

    const queryHasCypressDirective = hasDirectives([CYPRESS_DIRECTIVE], query)

    if (!queryHasCypressDirective) {
      return forward ? forward(operation) : null
    }

    const mainDefinition = getMainDefinition(query)
    const fragmentDefinitions = getFragmentDefinitions(query)
    const fragmentMap = createFragmentMap(fragmentDefinitions)

    const isRunningCypressTests =
      localStorage.getItem(localStorageKeys.isRunningCypressTests) === 'true'

    operation.query = removeUnusedFragmentDefinitions(
      removeCypressDirectivesFromNode({
        node: query,
        removeFieldsFromNodes: !isRunningCypressTests,
      })
    )

    if (forward) {
      return forward(operation).map(result => {
        if (!isRunningCypressTests) {
          replaceCypressDirectiveFieldsWithNull(
            result.data,
            fragmentMap,
            mainDefinition.selectionSet
          )
        }
        return result
      })
    }

    return null
  })
